import React from 'react';

export type StairsStackVariant = 'outline' | 'filled' | 'gradient';
export type StairsStackSize = 'sm' | 'md' | 'lg' | 'xl';

export interface StairsStackProps {
	// Core configuration
	variant?: StairsStackVariant; // 'outline', 'filled', or 'gradient'
	size?: StairsStackSize; // sm/md/lg/xl
	count?: 3 | 4 | 5 | 6 | 7; // number of steps (default 4)
	
	// Visual properties
	colors?: string[]; // array of colors for each step
	strokeWidth?: number; // border width (default 2)
	strokeColor?: string; // border color (default currentColor)
	fillOpacity?: number; // fill opacity for filled variant (default 1)
	stepOpacities?: number[]; // individual opacity for each step (overrides fillOpacity)
	showConnector?: boolean; // show the connecting path (default true)
	
	// Semantic labels (optional)
	labels?: string[]; // step labels for semantic meaning
	
	// Layout
	className?: string;
	style?: React.CSSProperties;
}

const sizeMap = {
	sm: 16,
	md: 24,
	lg: 32,
	xl: 48,
};

export const StairsStack: React.FC<StairsStackProps> = ({
	variant = 'outline',
	size = 'md',
	count = 4,
	colors = ['#5A7D5E', '#53675B', '#E3B505', '#F85E5E', '#9CA3AF', '#584E47', '#2F5C5B'], // brand colors
	strokeWidth = 2,
	strokeColor = 'currentColor',
	fillOpacity = 1,
	stepOpacities,
	showConnector = true,
	labels,
	className,
	style,
}) => {
	const iconSize = sizeMap[size];
	const stepCount = Math.min(count, colors.length);
	
	// Calculate step positions - use smaller steps for better fit with 7 steps
	// Each step is 8x3 (reduced from 10x4) with 1.5px horizontal offset
	// Distribute steps evenly across available vertical space (from y=3 to y=18)
	const steps = Array.from({ length: stepCount }).map((_, i) => {
		// Maintain consistent 1.5px horizontal indentation for all steps
		const x = 3 + (i * 1.5);
		// Distribute steps evenly across vertical space (15px available: 18-3)
		const availableVerticalSpace = 15; // from y=3 to y=18
		const stepHeight = 3;
		const totalStepHeight = stepCount * stepHeight;
		const spacing = stepCount > 1 ? (availableVerticalSpace - totalStepHeight) / (stepCount - 1) : 0;
		const y = 18 - (i * (stepHeight + spacing));
		const stepOpacity = stepOpacities?.[i] ?? fillOpacity;
		return {
			index: i,
			x,
			y,
			color: colors[i] || colors[0],
			opacity: stepOpacity,
			label: labels?.[i] || `Step ${i + 1}`,
		};
	});

	// Determine fill and stroke based on variant
	const getStepProps = (stepColor: string, stepOpacity: number) => {
		switch (variant) {
			case 'filled':
				return {
					fill: stepColor,
					fillOpacity: stepOpacity,
					stroke: strokeColor,
				};
			case 'gradient':
				return {
					fill: `url(#gradient-${stepColor.replace('#', '')})`,
					fillOpacity: stepOpacity,
					stroke: strokeColor,
				};
			case 'outline':
			default:
				return {
					fill: 'none',
					stroke: stepColor,
				};
		}
	};

	return (
		<div className={className} style={style}>
			<svg
				width={iconSize}
				height={iconSize}
				viewBox="0 0 24 24"
				fill="none"
				stroke={strokeColor}
				strokeWidth={strokeWidth}
				strokeLinecap="round"
				strokeLinejoin="round"
				role="img"
				aria-label={`Stairs Stack — ${stepCount} steps representing layered progression`}
			>
				{/* Gradient definitions for gradient variant */}
				{variant === 'gradient' && (
					<defs>
						{colors.slice(0, stepCount).map((color, i) => (
							<linearGradient key={i} id={`gradient-${color.replace('#', '')}`} x1="0%" y1="0%" x2="100%" y2="100%">
								<stop offset="0%" stopColor={color} stopOpacity={fillOpacity} />
								<stop offset="100%" stopColor={color} stopOpacity={fillOpacity * 0.7} />
							</linearGradient>
						))}
					</defs>
				)}
				
				{/* Render steps */}
				{steps.map((step) => (
					<rect
						key={step.index}
						width="8"
						height="3"
						x={step.x}
						y={step.y}
						{...getStepProps(step.color, step.opacity)}
					/>
				))}
				
				{/* Optional connecting path - a simple line connecting the steps */}
				{showConnector && stepCount > 1 && (
					<path 
						d={`M${steps[0].x + 8} ${steps[0].y + 1.5} L${steps[stepCount - 1].x + 8} ${steps[stepCount - 1].y + 1.5}`} 
						stroke={strokeColor} 
						strokeWidth={strokeWidth}
						fill="none"
					/>
				)}
				
				{/* Optional labels */}
				{labels && labels.length > 0 && (
					<g>
						{steps.map((step, i) => (
							<text
								key={`label-${i}`}
								x={step.x + 4}
								y={step.y + 1.5}
								textAnchor="middle"
								dominantBaseline="middle"
								fontSize="6"
								fill={variant === 'outline' ? step.color : strokeColor}
								opacity={0.8}
							>
								{step.label}
							</text>
						))}
					</g>
				)}
			</svg>
		</div>
	);
};
